import { useMemo } from "react";
import type { ConfiguratorSharedProps } from "../../widgetDefinition";
import { Collapse, DatePicker, InputNumber, Select, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";
import type {
  BodyMapComponentSpec,
  BodyMapQuestionKey,
  MetricOption,
  SubjectEntity,
} from "../../Processing/types";
import { ConfigPanelLayout } from "../shared/ConfigPanelLayout";
import { PreviewShell } from "../shared/PreviewShell";
import { PreviewDisclaimer } from "../shared/PreviewDisclaimer";
import { useConfiguratorSync } from "../shared/useConfiguratorSync";
import { SlicerTokenInput } from "../shared/SlicerTokenInput";
import { BodyMapView } from "@components/BodyMapView/BodyMapView";
import { MOCK_BODY_MAP_DATA } from "./mockBodyMapData";
import { isSectionBindable } from "../../Processing/controllerBindings";
import { SingleDateForm } from "../shared/SingleDateForm";
import { SinglePlayerForm } from "../shared/SinglePlayerForm";
import { SectionSummary } from "../shared/SectionSummary";
import {
  bodyMapReducer,
  createInitialState,
  hydrateFromSpec,
  formStateToSpec,
} from "./bodyMapState";

const { Panel } = Collapse;
const { Text } = Typography;

const BODY_MAP_KEY_PREFIX = "bodymap_";


export function BodyMapConfigurator({
  onSpecChange,
  existingSpec,
  metrics,
  getSubjectEntities,
}: ConfiguratorSharedProps<BodyMapComponentSpec>) {
  // The team's bodymap questions come through the wellness metrics — only those
  // keys are valid for this team, so the picker must be driven by them rather
  // than a hardcoded list (the backend rejects question_keys the team lacks).
  const questionKeyOptions = useMemo(
    () =>
      metrics
        .filter((m) => m.metricKey?.startsWith(BODY_MAP_KEY_PREFIX))
        .map((m) => ({ label: m.name, value: m.metricKey as BodyMapQuestionKey })),
    [metrics],
  );

  const initialState = useMemo(() => {
    const base = createInitialState();
    const available = questionKeyOptions.map((o) => o.value);
    if (available.length && !available.includes(base.question_key)) {
      base.question_key = available[0];
    }
    const firstPlayer = getSubjectEntities("player")[0];
    if (firstPlayer) {
      base.playerSubject = `player-${firstPlayer.id}`;
      base.title = firstPlayer.name;
    }
    return base;
  }, [questionKeyOptions, getSubjectEntities]);

  const [state, dispatch] = useConfiguratorSync({
    reducer: bodyMapReducer,
    existingSpec,
    hydrate: hydrateFromSpec,
    createInitial: createInitialState,
    initialState,
    toSpec: formStateToSpec,
    onSpecChange,
    metrics,
    getSubjectEntities,
  });

  const hasQuestions = questionKeyOptions.length > 0;

  const typeLabel =
    questionKeyOptions.find((o) => o.value === state.question_key)?.label ?? "Body Map";

  const selectedPlayerName = state.playerSubject
    ? getSubjectEntities("player").find((p) => `player-${p.id}` === state.playerSubject)?.name ??
      "1 selected"
    : "None selected";

  const offsetAbs = Math.abs(state.dateOffset);
  const dateSummary =
    state.dateMode === "absolute"
      ? state.fixedDate || "Pick a date"
      : state.dateOffset === 0
        ? "Today"
        : `${offsetAbs} day${offsetAbs === 1 ? "" : "s"} ago`;

  const form = (
    <>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #e8ecf0" }}>
        <Space size={8} align="center" style={{ width: "100%" }}>
          <Text style={{ fontSize: 12, minWidth: 40 }}>Title</Text>
          <SlicerTokenInput
            size="small"
            placeholder="e.g. J. Smith Soreness"
            value={state.title}
            onChange={(title) => dispatch({ type: "SET_TITLE", title })}
            maxLength={100}
            style={{ flex: 1 }}
          />
        </Space>
      </div>
      <Collapse
        accordion
        activeKey={state.expandedKey}
        onChange={(key) => dispatch({ type: "SET_EXPANDED_KEY", key })}
        style={{ borderRadius: 0, borderTop: "none" }}
      >
        <Panel
          key="__type"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">1</span>
              <Text strong style={{ fontSize: 13 }}>Body Map Type</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{typeLabel}</Text>
            </div>
          }
        >
          {hasQuestions ? (
            <Select
              size="small"
              options={questionKeyOptions}
              style={{ width: "100%" }}
              value={state.question_key || undefined}
              placeholder="Select a body map question"
              onChange={(v) => dispatch({ type: "SET_QUESTION_KEY", value: v })}
            />
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              This team has no body map questions available.
            </Text>
          )}
        </Panel>

        <Panel
          key="__player"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">2</span>
              <Text strong style={{ fontSize: 13 }}>Player</Text>
              <SectionSummary kind="single_player" controllerId={state.singlePlayerControllerId}
                own={selectedPlayerName ?? ""} getSubjectEntities={getSubjectEntities} />
            </div>
          }
        >
          <SinglePlayerForm
            value={{
              singlePlayerControllerId: state.singlePlayerControllerId,
              selectedPlayer: state.playerSubject,
            }}
            onChange={(patch) => {
              if ("singlePlayerControllerId" in patch) {
                dispatch({ type: "SET_PLAYER_CONTROLLER", controllerId: patch.singlePlayerControllerId });
                return;
              }
              const nextSubject = patch.selectedPlayer ?? "";
              const defaultTitle = nextSubject
                ? getSubjectEntities("player").find((p) => `player-${p.id}` === nextSubject)?.name
                : undefined;
              dispatch({ type: "SET_PLAYER", playerSubject: nextSubject, defaultTitle });
            }}
            getSubjectEntities={getSubjectEntities}
            bindable={isSectionBindable("body_map", "single_player")}
            description="Choose one athlete."
          />
        </Panel>

        <Panel
          key="__date"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">3</span>
              <Text strong style={{ fontSize: 13 }}>Date</Text>
              <SectionSummary kind="single_date" controllerId={state.singleDateControllerId}
                own={dateSummary} />
            </div>
          }
        >
          <SingleDateForm
            value={state}
            onChange={(patch) => dispatch({ type: "UPDATE_SINGLE_DATE", patch })}
            bindable={isSectionBindable("body_map", "single_date")}
            description="Body maps read one day."
          />
        </Panel>
      </Collapse>
    </>
  );

  return (
    <ConfigPanelLayout
      form={form}
      preview={
        <PreviewShell
          title={state.title || "Body Map"}
          titleAddon={<PreviewDisclaimer />}
          subtitle={`${typeLabel} · ${dateSummary}`}
          hasContent={hasQuestions}
          emptyText="This team has no body map questions"
        >
          <BodyMapView
            data={MOCK_BODY_MAP_DATA}
            question_key={state.question_key}
            playersPage={true}
            showBothSides
            showTitle={false}
            showDate={false}
            fitHeight
          />
        </PreviewShell>
      }
    />
  );
}
